
import React from 'react';
import { cn } from '@/lib/utils';
import { Shield, ShieldCheck, AlertTriangle, Wifi, WifiOff } from 'lucide-react';
import { motion } from 'framer-motion';

type ConnectionType = 'secure' | 'encrypted' | 'quality';
type ConnectionStatus = 'excellent' | 'good' | 'poor' | 'offline';

interface ConnectionStatusBadgeProps {
  type?: ConnectionType;
  status?: ConnectionStatus;
  strength?: ConnectionStatus;
  className?: string;
  minimal?: boolean;
}

const ConnectionStatusBadge: React.FC<ConnectionStatusBadgeProps> = ({ 
  type = 'quality', 
  status = 'excellent',
  strength,
  className,
  minimal = false
}) => {
  // Use strength if provided, otherwise use status (for backward compatibility)
  const effectiveStrength = strength || status;
  
  // Define colors and icons based on type and strength
  const getConfig = () => {
    if (type === 'secure' || type === 'encrypted') {
      return {
        icon: <ShieldCheck size={14} className="text-green-500" />,
        text: type === 'secure' ? "Secure" : "E2E Encrypted",
        bgColor: "bg-green-500/20",
        textColor: "text-green-500"
      };
    }
    
    if (type === 'quality') {
      switch (effectiveStrength) {
        case 'excellent':
          return {
            icon: <Wifi size={14} className="text-green-500" />,
            text: "Excellent",
            bgColor: "bg-green-500/20",
            textColor: "text-green-500"
          };
        case 'good':
          return {
            icon: <Wifi size={14} className="text-yellow-500" />,
            text: "Good",
            bgColor: "bg-yellow-500/20",
            textColor: "text-yellow-500"
          };
        case 'poor':
          return {
            icon: <Wifi size={14} className="text-orange-500" />,
            text: "Poor",
            bgColor: "bg-orange-500/20",
            textColor: "text-orange-500"
          };
        case 'offline':
          return {
            icon: <WifiOff size={14} className="text-red-500" />,
            text: "Offline",
            bgColor: "bg-red-500/20",
            textColor: "text-red-500"
          };
        default:
          return {
            icon: <AlertTriangle size={14} className="text-yellow-500" />,
            text: "Unknown",
            bgColor: "bg-yellow-500/20",
            textColor: "text-yellow-500"
          };
      }
    }
    
    return {
      icon: <Shield size={14} className="text-blue-500" />,
      text: "Connected",
      bgColor: "bg-blue-500/20",
      textColor: "text-blue-500"
    };
  };
  
  const config = getConfig();
  
  if (minimal) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className={cn(
          "rounded-full p-1",
          config.bgColor,
          className
        )}
      >
        {config.icon}
      </motion.div>
    );
  }
  
  return (
    <motion.div 
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn(
        "backdrop-blur-sm rounded-full py-1 px-3 flex items-center space-x-1 font-body letter-spacing-tight",
        config.bgColor,
        className
      )}
    >
      {config.icon}
      <span className={cn("text-xs font-medium", config.textColor)}>
        {config.text}
      </span>
    </motion.div>
  );
};

export default ConnectionStatusBadge;
